import { useState } from "react";
import { Button } from "@/components/shared/ui/button";
import { ThemeToggle } from "@/components/shared/ui/theme-toggle";
import { LanguageSelector } from "@/components/shared/ui/language-selector";
import { useAuth } from "@/hooks/useAuth";
import { LogOut, Menu } from "lucide-react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import {
  Sheet,
  SheetContent,
  SheetTrigger,
} from "@/components/shared/ui/sheet";

type NavigationProps = {
  publicView?: boolean;
};

/**
 * Public-facing navigation bar.
 * Used only on unauthenticated / public pages (landing, login, legal).
 * Authenticated users get the AppSidebar instead.
 */
export const Navigation = ({
  publicView: _publicView = false,
}: NavigationProps) => {
  const { profile, signOut } = useAuth();
  const { t } = useTranslation();
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const isAuthenticated = !!profile;

  return (
    <nav
      className="bg-background/80 backdrop-blur-sm border-b pt-safe-top sticky top-0 z-50"
      role="navigation"
      aria-label="Main"
    >
      <div className="container mx-auto px-4 sm:px-6 px-safe py-4">
        <a
          href="#main"
          className="sr-only focus:not-sr-only focus:absolute focus:top-2 focus:left-2 focus:z-50 focus:bg-primary focus:text-primary-foreground focus:px-3 focus:py-2 focus:rounded-sm"
        >
          {t("common.skipToContent", "Spring til indhold")}
        </a>
        <div className="flex items-center justify-between gap-2">
          <Link to="/" className="hover:opacity-90 transition-opacity">
            <span className="text-lg sm:text-xl font-semibold truncate">
              {t("navigation.title")}
            </span>
          </Link>

          <div className="flex items-center gap-2 sm:gap-4 flex-shrink-0">
            {/* Desktop controls */}
            <div className="hidden md:flex items-center gap-4">
              <LanguageSelector />
              <ThemeToggle />
              {isAuthenticated ? (
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={signOut}
                  aria-label={t("navigation.signOut")}
                >
                  <LogOut className="h-4 w-4" />
                </Button>
              ) : (
                <Button variant="default" size="sm" asChild>
                  <Link to="/login">{t("common.logIn")}</Link>
                </Button>
              )}
            </div>

            {/* Mobile sheet */}
            <Sheet open={isMenuOpen} onOpenChange={setIsMenuOpen}>
              <SheetTrigger asChild>
                <Button
                  variant="outline"
                  size="sm"
                  className="md:hidden min-touch-target"
                  aria-label="Navigation menu"
                  data-testid="nav-menu-mobile"
                >
                  <Menu className="h-5 w-5" />
                </Button>
              </SheetTrigger>
              <SheetContent
                side="right"
                className="w-[90vw] max-w-sm pb-safe-bottom px-safe !px-6"
              >
                <div className="flex flex-col space-y-4 mt-6">
                  <div className="flex items-center justify-end gap-3">
                    <LanguageSelector />
                    <ThemeToggle />
                  </div>

                  {isAuthenticated ? (
                    <Button
                      variant="outline"
                      onClick={() => {
                        setIsMenuOpen(false);
                        signOut();
                      }}
                      className="justify-start min-touch-target w-full"
                    >
                      <LogOut className="h-4 w-4 mr-3" />
                      {t("navigation.signOut")}
                    </Button>
                  ) : (
                    <Button
                      variant="default"
                      className="justify-start min-touch-target w-full"
                      asChild
                      onClick={() => setIsMenuOpen(false)}
                    >
                      <Link to="/login" data-testid="nav-login-mobile">
                        {t("common.logIn")}
                      </Link>
                    </Button>
                  )}
                </div>
              </SheetContent>
            </Sheet>
          </div>
        </div>
      </div>
    </nav>
  );
};
